import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { createLayoutTabsSlice } from './layoutTabsSlice';
import { createLayoutSplittersSlice } from './layoutSplittersSlice';
import { createLayoutTabGroupsSlice } from './layoutTabGroupsSlice';
import { partializeLayoutState } from './layoutStorePartialize';
import { useSkillDraftsStore } from './skillDraftsStore';
import { useChatTabsStore } from './chatTabsStore';
import { useChatInstancesStore } from './chatInstancesStore';
import { resetMainContentTabSubTabState } from '../utils/mainContentTabSubTabReset';
import { createTenantPersistConfig, PERSIST_STORE_KEYS } from './persist';
import {
  APPLICATION_SPECIFIC_TAB_TYPES,
  type LayoutState,
  type MainContentTab,
  type MainContentTabData,
  type OpenMainContentTabInput,
  type SidebarTab,
  type Tab,
  type TabPageEntry,
} from './layoutStoreTypes';

// Helper function to map main content tab type to sidebar tab
function getSidebarTabForMainContentTab(
  tabType: MainContentTab,
): SidebarTab | null {
  const mapping: Partial<Record<MainContentTab, SidebarTab>> = {
    application: 'dashboard',
    'network-graph': 'network-graph',
    'signals-list': 'signals',
    'assets-list': 'assets',
    'findings-list': 'findings',
    'reports-list': 'reports',
    'executions-list': 'executions',
    requests: 'requests',
    websocket: 'websocket',
    intercept: 'intercept',
    'match-replace': 'match-replace',
    replay: 'replay',
    comparer: 'comparer',
    interact: 'interact',
    'proxy-settings': 'proxy-settings',
    settings: 'settings',
    welcome: 'dashboard',
    'code-list': 'code',
    'skills-list': 'skills',
    skill: 'skills',
    'skill-editor': 'skills',
    'agent-executions-list': 'agent-executions',
  };

  return mapping[tabType] || null;
}

const createTabPageEntryFromOpenInput = <TType extends MainContentTab>({
  tab,
}: {
  tab: OpenMainContentTabInput & { type: TType };
}): TabPageEntry => {
  return {
    pageId: tab.id,
    title: tab.title,
    type: tab.type,
    data: tab.data,
  } as TabPageEntry;
};

const createTabPageEntryFromTab = <TType extends MainContentTab>({
  tab,
}: {
  tab: Tab & { type: TType };
}): TabPageEntry => {
  return {
    pageId: tab.id,
    title: tab.title,
    type: tab.type,
    data: tab.data,
  } as TabPageEntry;
};

const applyPageEntryToTab = ({
  tab,
  pageEntry,
}: {
  tab: Tab;
  pageEntry: TabPageEntry;
}): Tab => {
  const nextTab = {
    ...tab,
    title: pageEntry.title,
    type: pageEntry.type,
  } as Tab;

  if (pageEntry.data === undefined) {
    const { data: _data, ...tabWithoutData } = nextTab as Tab & {
      data?: MainContentTabData;
    };

    return tabWithoutData as Tab;
  }

  return {
    ...nextTab,
    data: pageEntry.data,
  } as Tab;
};

const getNewTabInsertIndex = ({
  tabs,
  activeTabId,
}: {
  tabs: Tab[];
  activeTabId: string | undefined;
}): number => {
  const activeTabIndex = tabs.findIndex((tab) => tab.id === activeTabId);
  const activeTab = tabs[activeTabIndex];

  if (activeTab?.isPinned) {
    const lastPinnedIndex = tabs.findLastIndex((tab) => tab.isPinned);

    return lastPinnedIndex + 1;
  }

  if (activeTabIndex >= 0) {
    return activeTabIndex + 1;
  }

  return tabs.length;
};

const generateTabId = (): string => {
  const entropy = Math.random().toString(36).slice(2, 8);

  return `${Date.now().toString(36)}-${entropy}`;
};

const handleMainContentTabClosed = ({
  closingTab,
}: {
  closingTab: Tab;
}): void => {
  resetMainContentTabSubTabState({ tab: closingTab });

  if (closingTab.type !== 'skill-editor') {
    return;
  }

  const { skillDraftId } = closingTab.data;

  useSkillDraftsStore.getState().removeDraft(skillDraftId);
  const chatTabsStore = useChatTabsStore.getState();
  const chatInstancesStore = useChatInstancesStore.getState();

  chatTabsStore.chatTabs
    .filter((chatTab) => chatTab.skillDraftId === skillDraftId)
    .forEach((chatTab) => {
      chatTabsStore.updateChatTab(chatTab.tabId, {
        skillDraftId: undefined,
      });
      chatInstancesStore.updateConfig(chatTab.chatId, {
        skillDraftId: undefined,
      });
    });
};

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedSidebarTab: 'dashboard',

      detachedTabIds: [],
      isCopilotDetached: false,

      showSidePanels: true,

      // Individual panel collapsed state (VSCode-style minimize)
      isDomainTreeCollapsed: false,
      isChatCollapsed: false,
      isReplayCollectionsCollapsed: false,
      isAiSignalsKindNavCollapsed: false,
      isSidebarMinimized: false,

      // Right panel view preference
      rightPanelView: 'copilot' as const,

      // Endpoint tab state
      endpointTabs: {},

      // WebPage tab state
      webPageTabs: {},

      tabSections: {},

      setSidebarTab: (tab) => {
        set({ selectedSidebarTab: tab });
      },
      ...createLayoutTabsSlice({
        set,
        get,
        applicationSpecificTabTypes: APPLICATION_SPECIFIC_TAB_TYPES,
        getSidebarTabForMainContentTab,
        createTabPageEntryFromOpenInput,
        createTabPageEntryFromTab,
        applyPageEntryToTab,
        getNewTabInsertIndex,
        generateTabId,
        onMainContentTabClosed: handleMainContentTabClosed,
      }),
      ...createLayoutSplittersSlice({ set }),
      ...createLayoutTabGroupsSlice({ set, get }),

      markTabDetached: (tabId) => {
        const { detachedTabIds } = get();

        if (detachedTabIds.includes(tabId)) {
          return;
        }

        set({ detachedTabIds: [...detachedTabIds, tabId] });
      },
      markTabReattached: (tabId) => {
        set({
          detachedTabIds: get().detachedTabIds.filter((id) => id !== tabId),
        });
      },
      markCopilotDetached: () => set({ isCopilotDetached: true }),
      markCopilotReattached: () => set({ isCopilotDetached: false }),

      setSidePanelsVisible: (visible) => set({ showSidePanels: visible }),
      setDomainTreeCollapsed: (collapsed) =>
        set({ isDomainTreeCollapsed: collapsed }),
      setChatCollapsed: (collapsed) => set({ isChatCollapsed: collapsed }),
      setReplayCollectionsCollapsed: (collapsed) =>
        set({ isReplayCollectionsCollapsed: collapsed }),
      setAiSignalsKindNavCollapsed: (collapsed) =>
        set({ isAiSignalsKindNavCollapsed: collapsed }),
      toggleDomainTreeCollapsed: () => {
        const { isDomainTreeCollapsed } = get();

        set({ isDomainTreeCollapsed: !isDomainTreeCollapsed });
      },
      toggleChatCollapsed: () => {
        const { isChatCollapsed } = get();

        set({ isChatCollapsed: !isChatCollapsed });
      },
      toggleReplayCollectionsCollapsed: () => {
        const { isReplayCollectionsCollapsed } = get();

        set({ isReplayCollectionsCollapsed: !isReplayCollectionsCollapsed });
      },
      toggleAiSignalsKindNavCollapsed: () => {
        const { isAiSignalsKindNavCollapsed } = get();

        set({ isAiSignalsKindNavCollapsed: !isAiSignalsKindNavCollapsed });
      },
      setSidebarMinimized: (minimized) =>
        set({ isSidebarMinimized: minimized }),
      toggleSidebarMinimized: () => {
        const { isSidebarMinimized } = get();

        set({ isSidebarMinimized: !isSidebarMinimized });
      },

      // Right panel view preference actions
      setRightPanelView: (view) => set({ rightPanelView: view }),

      // Endpoint tab actions
      setEndpointTab: (endpointId, tab) => {
        const { endpointTabs } = get();

        set({
          endpointTabs: {
            ...endpointTabs,
            [endpointId]: tab,
          },
        });
      },

      // WebPage tab actions
      setWebPageTab: (webPageId, tab) => {
        const { webPageTabs } = get();

        set({
          webPageTabs: {
            ...webPageTabs,
            [webPageId]: tab,
          },
        });
      },

      setTabSection: ({ scope, tabId, sectionId }) => {
        const { tabSections } = get();
        const scopeEntries = tabSections[scope] ?? {};

        set({
          tabSections: {
            ...tabSections,
            [scope]: {
              ...scopeEntries,
              [tabId]: sectionId,
            },
          },
        });
      },
      clearTabSection: ({ scope, tabId }) => {
        const { tabSections } = get();
        const scopeEntries = tabSections[scope];

        if (!scopeEntries || scopeEntries[tabId] === undefined) return;
        const { [tabId]: _removed, ...rest } = scopeEntries;
        const nextTabSections = { ...tabSections };

        if (Object.keys(rest).length === 0) {
          delete nextTabSections[scope];
        } else {
          nextTabSections[scope] = rest;
        }

        set({ tabSections: nextTabSections });
      },
    }),
    {
      ...createTenantPersistConfig({ storeKey: PERSIST_STORE_KEYS.layout }),
      partialize: partializeLayoutState,
    },
  ),
);
